/*
Based on the ChopLines component: https://github.com/dw2/chop-lines

MIT License:

Copyright 2019 Softwire Ltd.

Permission is hereby granted, free of charge, to any person obtaining a copy of this software
and associated documentation files (the "Software"),
to deal in the Software without restriction,
including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense,
and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so,
subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY,
WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE
OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/

import React from 'react';
import debounce from 'lodash.debounce';
import { AutoSizer, Ellipsis, Wrapper } from './components';

declare interface ChopLinesProps {
    lines?: number;
    lineHeight?: number;
    maxHeight?: number;
    background?: string;
    ellipsis?: any;
    children: any;
}

declare interface ChopLinesState {
    height: number;
}

class ChopLines extends React.Component<ChopLinesProps, ChopLinesState> {
  autoSizer?: HTMLElement;
  state: ChopLinesState = { height: 0 };

  onResize: () => void = debounce(() => {
    try {
      window.requestAnimationFrame(this.measureAutoSizer);
    } catch (e) {}
  }, 50);

  componentDidMount(): void {
    this.measureAutoSizer();
    try {
      window.addEventListener('resize', this.onResize);
    } catch (e) {}
  }

  componentWillUnmount(): void {
    try {
      window.removeEventListener('resize', this.onResize);
    } catch (e) {}
  }

  get doesOverflow(): boolean {
    const { lineHeight, lines, maxHeight } = this.props;
    const { height } = this.state;

    return height > (maxHeight || (lineHeight || 0) * (lines || 0));
  }

  setAutoSizerRef = (autoSizer: HTMLElement) => {
    this.autoSizer = autoSizer;
  }

  measureAutoSizer = () => {
    if (!this.autoSizer) {
        return;
    }

    const dimensions = this.autoSizer.getBoundingClientRect();
    const { height } = dimensions;

    if (height === this.state.height) {
        return;
    }
    this.setState({ height });
  }

  render(): JSX.Element {
    const {
      lines = 0,
      lineHeight = 0,
      maxHeight = 0,
      ellipsis = '…',
      children,
      ...rest
    } = this.props;
    const maxHeightPx = `${maxHeight || lineHeight * lines}px`;

    return (
      <Wrapper
        {...rest}
        lineHeight={maxHeight ? 'unset' : `${lineHeight}px`}
        maxHeight={this.doesOverflow ? maxHeightPx : ''}
        overflow={this.doesOverflow ? 'hidden' : 'unset'}
      >
        <AutoSizer forwardedRef={this.setAutoSizerRef} aria-hidden={true}>
          {children}
        </AutoSizer>
        {children}
        {this.doesOverflow && <Ellipsis>{ellipsis}</Ellipsis>}
      </Wrapper>
    );
  }
}

export default ChopLines;
